import { useState, useCallback } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";
import { useAuth } from "../../contexts/AuthContext";
import { PageHeader } from "../../components/common/PageHeader";
import {
  useKbListFiles,
  useKbDeleteFile,
  useKbReindexFile,
  useKbGetUploadUrl,
  useKbCreateFile,
  getKbListFilesQueryKey,
} from "../../api/generated/generated";
import type { FileResponse, UploadUrlResponse } from "../../api/generated";
import { getErrorMessage } from "../../api/client";

const ACCEPTED_TYPES: Record<string, string[]> = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "text/plain": [".txt"],
  "text/markdown": [".md"],
  "text/csv": [".csv"],
};

const MAX_SIZE = 100 * 1024 * 1024; // 100MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function statusColor(
  status: string,
): "success" | "warning" | "error" | "info" | "default" {
  switch (status) {
    case "indexed":
      return "success";
    case "processing":
    case "pending":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

export function AdminKnowledgeBasePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const files = useKbListFiles({
    query: {
      refetchInterval: (query) => {
        const files = query.state.data?.data;
        const hasProcessing = files?.some(
          (f) => f.status === "pending" || f.status === "processing",
        );
        return hasProcessing ? 5000 : false;
      },
    },
  });

  const getUploadUrl = useKbGetUploadUrl();
  const createFile = useKbCreateFile();
  const uploadFile = useMutation({
    mutationFn: async (file: File) => {
      const contentType = file.type || "application/octet-stream";
      const urlResp = await getUploadUrl.mutateAsync({
        data: {
          filename: file.name,
          content_type: contentType,
          size_bytes: file.size,
        },
      });
      const urlData = urlResp.data as UploadUrlResponse;
      await fetch(urlData.upload_url, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });
      const fileResp = await createFile.mutateAsync({
        data: {
          filename: file.name,
          content_type: contentType,
          size_bytes: file.size,
          storage_path: urlData.storage_path,
        },
      });
      return fileResp.data as FileResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getKbListFilesQueryKey() });
    },
  });

  const deleteFile = useKbDeleteFile({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getKbListFilesQueryKey() });
      },
    },
  });

  const reindexFile = useKbReindexFile({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getKbListFilesQueryKey() });
      },
    },
  });

  const [deleteTarget, setDeleteTarget] = useState<FileResponse | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      for (const file of acceptedFiles) {
        uploadFile.mutate(file, {
          onSuccess: () => {
            setSnackbar({
              open: true,
              message: `Uploaded ${file.name}`,
              severity: "success",
            });
          },
          onError: (error) => {
            setSnackbar({
              open: true,
              message: getErrorMessage(error),
              severity: "error",
            });
          },
        });
      }
    },
    [uploadFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_SIZE,
    multiple: true,
  });

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteFile.mutate(
      { fileId: deleteTarget.id },
      {
        onSuccess: () => {
          setSnackbar({
            open: true,
            message: "File deleted",
            severity: "success",
          });
          setDeleteTarget(null);
        },
        onError: (error) => {
          setSnackbar({
            open: true,
            message: getErrorMessage(error),
            severity: "error",
          });
          setDeleteTarget(null);
        },
      },
    );
  };

  if (!user?.superAdmin) {
    return <Alert severity="warning">Super admin access required.</Alert>;
  }

  return (
    <Box>
      <PageHeader
        title="System Knowledge Base"
        subtitle="Manage system-wide documents available to all organizations via RAG search."
      />
      {/* Upload area */}
      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          mb: 3,
          textAlign: "center",
          cursor: "pointer",
          border: "2px dashed",
          borderColor: isDragActive ? "primary.main" : "divider",
          bgcolor: isDragActive ? "action.hover" : "background.paper",
          transition: "all 0.2s",
        }}
      >
        <input {...getInputProps()} />
        <UploadFileIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
        <Typography variant="body1">
          {isDragActive
            ? "Drop files here..."
            : "Drag & drop files here, or click to browse"}
        </Typography>
        <Typography variant="caption" sx={{
          color: "text.secondary"
        }}>
          PDF, DOCX, TXT, MD, CSV &middot; Max 100MB per file
        </Typography>
        {uploadFile.isPending && <CircularProgress size={20} sx={{ ml: 1 }} />}
      </Paper>
      {/* File list */}
      {files.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : files.isError ? (
        <Alert severity="error">Failed to load files.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Filename</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Chunks</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(files.data?.data ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        py: 2
                      }}>
                      No files uploaded yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                (files.data?.data ?? []).map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>
                      <Typography variant="body2">{file.filename}</Typography>
                    </TableCell>
                    <TableCell>{formatFileSize(file.size_bytes)}</TableCell>
                    <TableCell>{file.chunk_count}</TableCell>
                    <TableCell>
                      <Chip
                        label={file.status}
                        size="small"
                        color={statusColor(file.status)}
                        variant="outlined"
                      />
                      {file.error && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ display: "block" }}
                        >
                          {file.error}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(file.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        title="Re-index"
                        onClick={() => reindexFile.mutate({ fileId: file.id })}
                        disabled={
                          file.status === "processing" ||
                          file.status === "pending"
                        }
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Delete"
                        onClick={() => setDeleteTarget(file)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Delete confirmation */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.filename}"? This
            will permanently remove the file and all its indexed chunks. This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteFile.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
